import * as React from "react";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import Loading from "../../components/loading";
import {gql} from "@apollo/client";
import {query} from "../../lib/apollo";
import {
    Alert,
    FormControl,
    IconButton,
    Link as MuiLink,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import moment from "moment";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import MultiSelect from "../../components/multiSelect/multiSelect";
import {searchPubs} from "../blockedPublishers/blockedPublishersService";
import downloadCsv from "../../lib/downloadCsv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {Link} from "react-router-dom";
import {toast} from "react-toastify";

type FeedbackRow = {
    id: number;
    date: string;
    publisherId: number;
    publisherName: string;
    rating: number;
    comment?: string | null;
    email?: string | null;
    url: string;
    ip?: string | null;
    userAgent?: string | null;
};

const RATINGS = [
    {id: 1, value: "1"},
    {id: 2, value: "2"},
    {id: 3, value: "3"},
    {id: 4, value: "4"},
    {id: 5, value: "5"},
];

async function searchRatings(search: string, selected: any[]) {
    const selectedIds = selected.map((x) => x.id);
    return RATINGS.filter((x) =>
        !selectedIds.includes(x.id) && x.value.includes((search || "").trim())
    );
}

export default function FeedbackPage() {
    React.useEffect(() => {
        document.title = "Feedback | DN";
    }, []);

    const [dateRange, setDateRange] = React.useState([] as any);
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [results, setResults] = React.useState([] as FeedbackRow[]);
    const [selectedPublishers, setSelectedPublishers] = React.useState([] as any[]);
    const [selectedRatings, setSelectedRatings] = React.useState([] as any[]);
    const [search, setSearch] = React.useState("");

    React.useEffect(() => {
        const d = RangeToTimes("Last 7 Days");
        setDateRange(d);
        setFiltersLoaded(true);
        reload(d, [], [], "").then(() => {});
    }, []);

    const reload = async (
        date = dateRange,
        pubs = selectedPublishers,
        ratings = selectedRatings,
        searchVal = search,
    ) => {
        if (!date || date.length < 2) {
            toast.error("Select a date range");
            return;
        }
        setLoading(true);
        const q = gql`
            query q($input: PathFeedbackListInput!) {
                pathFeedbackList(input: $input) {
                    id
                    date
                    publisherId
                    publisherName
                    rating
                    comment
                    email
                    url
                    ip
                    userAgent
                }
            }
        `;
        const variables = {
            input: {
                startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
                endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
                publisherIds: pubs.map((x: any) => x.id),
                ratings: ratings.map((x: any) => x.id),
                search: searchVal.trim() || null,
            },
        };
        const response = await query(q, variables);
        setResults((response && response.pathFeedbackList) || []);
        setLoading(false);
    };

    const download = async () => {
        const content: any[][] = [[
            "Date", "Publisher", "PublisherId", "Rating", "Comment", "Email", "URL", "IP", "User-Agent",
        ]];
        results.forEach((r) => {
            content.push([
                r.date,
                r.publisherName,
                r.publisherId,
                r.rating,
                r.comment || "",
                r.email || "",
                r.url,
                r.ip || "",
                r.userAgent || "",
            ]);
        });
        await downloadCsv("feedback.csv", content);
    };

    if (!filtersLoaded) {
        return <Loading/>;
    }

    return (
        <>
            <h2>Feedback</h2>
            <Box sx={{display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end", gap: 1}}>
                <DateRange dateRange={dateRange} setDateRange={setDateRange} style={{marginRight: "10px"}}/>
                <FormControl variant="standard" sx={{width: "200px"}}>
                    <MultiSelect
                        searchFunction={searchPubs}
                        selectedItems={selectedPublishers}
                        setSelectedItems={setSelectedPublishers}
                        placeholder={"Publishers"}
                    />
                </FormControl>
                <FormControl variant="standard" sx={{width: "120px"}}>
                    <MultiSelect
                        searchFunction={searchRatings}
                        selectedItems={selectedRatings}
                        setSelectedItems={setSelectedRatings}
                        placeholder={"Rating"}
                    />
                </FormControl>
                <TextField
                    variant="standard"
                    label="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{width: "180px"}}
                    placeholder="email, comment, IP"
                />
                <Button
                    onClick={() => reload()}
                    variant="contained"
                    color="success"
                    disabled={loading}
                >
                    Run
                </Button>
                {results.length > 0 && (
                    <IconButton aria-label="download" onClick={download} title="Download CSV">
                        <FileDownloadIcon/>
                    </IconButton>
                )}
            </Box>
            <p></p>
            {loading ? <Loading/> :
                results.length === 0 ? <Alert severity="warning">No results found.</Alert> :
                    <TableContainer component={Paper} sx={{maxWidth: "calc(100vw - 10px)", maxHeight: "calc(100vh - 300px)"}}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableHeaderRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Publisher</TableCell>
                                    <TableCell>Rating</TableCell>
                                    <TableCell>Comment</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>URL</TableCell>
                                    <TableCell>IP</TableCell>
                                    <TableCell>User Agent</TableCell>
                                </TableHeaderRow>
                            </TableHead>
                            <TableBody>
                                {results.map((row) => (
                                    <StripedTableRow key={row.id}>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>
                                            {row.date ? moment(row.date).format("YYYY-MM-DD HH:mm") : ""}
                                        </TableCell>
                                        <TableCell>
                                            <MuiLink component={Link} to={`/publishers/${row.publisherId}`}>
                                                {row.publisherName || "?"} ({row.publisherId})
                                            </MuiLink>
                                        </TableCell>
                                        <TableCell>{row.rating}</TableCell>
                                        <TableCell sx={{maxWidth: 280, wordBreak: "break-word"}}>
                                            {row.comment || ""}
                                        </TableCell>
                                        <TableCell>
                                            {row.email ? (
                                                <MuiLink
                                                    href={`/lead?email=${encodeURIComponent(row.email)}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {row.email}
                                                </MuiLink>
                                            ) : ""}
                                        </TableCell>
                                        <TableCell sx={{maxWidth: 260, wordBreak: "break-all"}}>
                                            {row.url ? (
                                                <MuiLink href={row.url} target="_blank" rel="noreferrer">
                                                    {row.url}
                                                </MuiLink>
                                            ) : ""}
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>{row.ip || ""}</TableCell>
                                        <TableCell sx={{maxWidth: 280, wordBreak: "break-word"}}>
                                            {row.userAgent || ""}
                                        </TableCell>
                                    </StripedTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
            }
        </>
    );
}
